import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types/customer";
import { InspectionList } from "@/components/properties/inspection-list";
import { LettersAboutCard } from "@/components/properties/letters-about-card";
import {
  buttonClasses,
  ContactBlock,
  DeviceRow,
  LoadError,
  PageTitle,
  RowList,
  Skeleton,
} from "@/components/ui";
import { useActiveMembership, useMyCustomerContext } from "@/hooks/use-customer-context";
import { useCustomerInstallations } from "@/hooks/use-customer-devices";
import { useCustomerFacility } from "@/hooks/use-customer-premises";
import { REQUEST_PAGE_LIMIT, useCustomerTestRequests } from "@/hooks/use-customer-test-requests";
import { useCustomerInspections } from "@/hooks/use-customer-tests";
import { deviceLink, propertiesLink } from "@/lib/destinations";
import { sortDevices } from "@/lib/device-list";
import { openRequestIndex } from "@/lib/home-standing";
import { brandingFromMembership } from "@/lib/portal-memory";
import { present } from "@/lib/present";

type CustomerFacility = components["schemas"]["CustomerFacility"];

export const Route = createFileRoute("/_customer/properties/$facilityId/")({
  component: PropertyRecordPage,
});

const PAGE = "flex flex-col gap-5 lg:gap-6";
const GRID = "grid items-start gap-4.5 lg:grid-cols-[1fr_340px] lg:gap-6";
const COLUMN = "flex min-w-0 flex-col gap-4.5 lg:gap-5.5";
const SECTION_TITLE = "mb-2.5 text-lg font-semibold tracking-[-0.01em] text-ink";

// The request feed Home reads, so the record shares its cache entry. It takes
// no account: the property itself narrows it.
const REQUEST_PARAMS = { limit: REQUEST_PAGE_LIMIT };

// The loaded page's shape in static blocks: the title, the device list, then
// the side column's cards.
const LOADING = (
  <div className={PAGE}>
    <div>
      <Skeleton height={34} width="55%" />
      <Skeleton height={18} width="40%" className="mt-2" />
    </div>
    <div className={GRID}>
      <div className={COLUMN}>
        <section>
          <Skeleton width={120} height={20} className="mb-2.5" />
          <div className="divide-y divide-line rounded-card border border-line bg-surface">
            {[0, 1, 2].map((row) => (
              <div key={row} className="flex items-center gap-3 px-3.5 py-3 lg:px-4.5">
                <Skeleton width={44} height={36} className="shrink-0 rounded-control" />
                <div className="flex-1 space-y-2">
                  <Skeleton width="60%" />
                  <Skeleton width="40%" height={12} />
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
      <div className={COLUMN}>
        <Skeleton variant="card" height={140} />
        <Skeleton variant="card" height={160} />
      </div>
    </div>
  </div>
);

// Line 1, then the city and state, each when present.
function addressOf(facility: CustomerFacility): string | null {
  const parts = [present(facility.address_line1), present(facility.city), present(facility.state)];
  const line = parts.filter((part): part is string => part !== null).join(", ");
  return line || null;
}

function PropertyRecordPage() {
  const { t } = useTranslation("customer");
  const { facilityId } = Route.useParams();
  const active = useActiveMembership();
  const { data: context } = useMyCustomerContext();
  // Every read keys on the property itself, never on the active account: a
  // record reached from another of the person's accounts (after a switch, or
  // through a link) reads whole, and RLS fences each read to what the person
  // can see.
  const facilityRead = useCustomerFacility(facilityId);
  const devicesRead = useCustomerInstallations({ facility_id: facilityId });
  const requestsRead = useCustomerTestRequests(REQUEST_PARAMS);
  const inspectionsRead = useCustomerInspections({ facility_id: facilityId });

  // A 400 is a malformed id (a truncated link, `/properties/devices` with no
  // id): no read of it can succeed, so it reads as not found, as a 404 does.
  const missing = facilityRead.error?.status === 404 || facilityRead.error?.status === 400;
  if (missing) {
    return (
      <div
        role="alert"
        className="rounded-card border border-line bg-surface p-4 shadow-card lg:p-5"
      >
        <h1 className="text-lg font-semibold tracking-[-0.01em] text-ink">
          {t("propertyDetail.notFound")}
        </h1>
        <Link {...propertiesLink()} className={`mt-3 ${buttonClasses("secondary")}`}>
          {t("properties.title")}
        </Link>
      </div>
    );
  }

  // The property's own read settles first: a read keyed by a malformed id
  // fails too, and must not report a retryable failure before the not-found.
  if (facilityRead.isPending) return LOADING;

  if (
    facilityRead.isError ||
    devicesRead.isError ||
    requestsRead.isError ||
    inspectionsRead.isError
  ) {
    return (
      <LoadError
        asPage
        title={t("propertyDetail.error")}
        body={t("loadError.body")}
        onRetry={() => {
          void facilityRead.refetch();
          void devicesRead.refetch();
          void requestsRead.refetch();
          void inspectionsRead.refetch();
        }}
      />
    );
  }

  const facility = facilityRead.data;
  if (!facility || devicesRead.isPending || requestsRead.isPending || inspectionsRead.isPending) {
    return LOADING;
  }

  // The property belongs to its OWN utility, whichever account is active: the
  // membership naming the org that owns it names the utility, dates the page
  // and gives the program contact. The active membership stands in only when
  // no membership names that org.
  const owning = context?.memberships.find((m) => m.org_id === facility.org_id) ?? active;
  const org = owning?.org_name ?? "";
  const timezone = owning?.org_timezone;
  const address = addressOf(facility);
  const here = sortDevices(
    devicesRead.data?.items ?? [],
    "urgency",
    new Map([[facility.id, facility]]),
  );
  // Each device's earliest open request here, whichever of the person's
  // accounts made it.
  const openRequestByDevice = openRequestIndex(requestsRead.data?.items ?? [], [facility]);

  return (
    <div className={PAGE}>
      <PageTitle
        title={facility.name}
        sub={address ?? undefined}
        back={{ label: t("properties.title"), link: propertiesLink() }}
        trail={[{ label: t("properties.title"), link: propertiesLink() }, { label: facility.name }]}
      />
      <div className={GRID}>
        <div className={COLUMN}>
          <section>
            <h2 className={SECTION_TITLE}>{t("propertyDetail.devices")}</h2>
            {here.length > 0 ? (
              <RowList>
                {here.map((installation) => (
                  <DeviceRow
                    key={installation.id}
                    installation={installation}
                    link={deviceLink(installation.id)}
                    openRequest={openRequestByDevice.get(installation.id) ?? null}
                    timezone={timezone}
                  />
                ))}
              </RowList>
            ) : (
              <p className="text-sm text-muted">{t("propertyDetail.devicesEmpty")}</p>
            )}
          </section>
          <InspectionList inspections={inspectionsRead.data?.items ?? []} timezone={timezone} />
        </div>
        <div className={COLUMN}>
          <LettersAboutCard
            title={t("propertyDetail.letters")}
            emptyText={t("propertyDetail.lettersEmpty", { org })}
            orgName={org}
            timezone={timezone}
            facilityId={facility.id}
            filter={{ facility_id: facility.id }}
          />
          {owning && <ContactBlock branding={brandingFromMembership(owning)} />}
        </div>
      </div>
    </div>
  );
}
